// =============================================================================
// AGC: [SOURCE_OF_TRUTH] CANONICAL LOT SERVICES
// AGC: This file contains the SINGLE SOURCE OF TRUTH implementations for:
// AGC:   - recommendCropsForLot(lotId)
// AGC:   - generatePlanForLot(lotId, chosenCrop)
// AGC:   - computeWeatherHealth(lotId)
// AGC:
// AGC: ALL screens in the main user flow MUST use these functions.
// AGC: Any other implementations are DEPRECATED and should be removed.
// =============================================================================

import { getBackendUrl } from '../utils/backendUrl'
import { generateFlowRunId, logFlowTrace } from '../utils/flowTrace'
import type {
  Plot,
  CropType,
  CropRecommendation,
  LotWeatherHealth,
  ClimateProfile,
} from '../types'
import type { CropPlan, RequiredInput, RequiredInputsSummary } from '../types/cropPlan'
import type { TaskInputEntry, InputSnapshot } from '../types/task'
import type { TaskInput } from '../types/admin/models'
import { generateRequiredInputs, createInputsSummary, createCropPlanLocal } from './cropPlanService'
import { calculateTotalQuantity } from '../types/unitConversion'

// =============================================================================
// AGC: [SOURCE_OF_TRUTH] TYPES
// =============================================================================

export interface RecommendCropsResult {
  success: boolean
  recommendations: CropRecommendation[]
  locationClimate: ClimateProfile | null
  targetMonth: number
  yearsAnalyzed: number
  flowRunId: string
  error?: string
}

export interface GeneratePlanResult {
  success: boolean
  plan: CropPlan | null
  inputs: RequiredInput[]
  summary: RequiredInputsSummary | null
  tasks: GeneratedTask[]
  flowRunId: string
  error?: string
}

export interface ComputeWeatherHealthResult {
  success: boolean
  health: LotWeatherHealth | null
  flowRunId: string
  error?: string
}

export interface GeneratedTask {
  id: string
  title: string
  category: string
  startDate: string
  endDate: string
  status: string
  plotId: string
  fieldId: string
  lotId?: string                  // AGC: Link to lot for area calculations
  templateId?: string             // AGC: Which template generated this task
  inputs: TaskInputEntry[]        // AGC: Inputs with quantities from template
  order?: number                  // AGC: Task order in plan sequence
}

export interface LotContext {
  lotId: string
  fieldId: string
  userId: string
  token?: string
  lot?: Plot
}

// =============================================================================
// AGC: [SOURCE_OF_TRUTH] CONFIGURATION
// =============================================================================

const BASE_URL = getBackendUrl()

// AGC: Feature flags for development
export const DEV_FLAGS = {
  USE_MOCK_RECOMMENDATIONS: false,
  USE_MOCK_WEATHER: false,
  ENABLE_LEGACY_SCREENS: import.meta.env.DEV === true,
}

// =============================================================================
// AGC: [SOURCE_OF_TRUTH] CANONICAL FUNCTION #1: recommendCropsForLot
// =============================================================================

/**
 * AGC: [SOURCE_OF_TRUTH] Get crop recommendations for a specific lot
 * AGC: This is THE canonical function for crop recommendations.
 * AGC: Uses backend recommendationService.js with geo+climate data.
 *
 * @param context - Lot context with IDs and auth
 * @param options - Additional options
 * @returns Crop recommendations with climate profile
 *
 * @example
 * const result = await recommendCropsForLot({
 *   lotId: 'plot-123',
 *   fieldId: 'field-456',
 *   userId: 'user-789',
 *   token: 'auth-token',
 *   lot: plotObject
 * })
 */
export async function recommendCropsForLot(
  context: LotContext,
  options: {
    plantingMonth?: number
    yearsBack?: number
    flowRunId?: string
  } = {}
): Promise<RecommendCropsResult> {
  const { lotId, fieldId, userId, token, lot } = context
  const { plantingMonth, yearsBack = 5, flowRunId: existingFlowRunId } = options

  // AGC: [FLOW_TRACE] Use existing flowRunId or generate new one
  const flowRunId = existingFlowRunId || generateFlowRunId()
  logFlowTrace(flowRunId, 'RECOMMEND_CROPS_START', { lotId, fieldId })

  try {
    // AGC: Validate lot has coordinates
    if (!lot?.coordinates || lot.coordinates.length < 3) {
      logFlowTrace(flowRunId, 'RECOMMEND_CROPS_ERROR', { error: 'Invalid coordinates' })
      return {
        success: false,
        recommendations: [],
        locationClimate: null,
        targetMonth: plantingMonth || new Date().getMonth() + 1,
        yearsAnalyzed: 0,
        flowRunId,
        error: 'Lot must have at least 3 coordinates to generate recommendations',
      }
    }

    // AGC: Convert coordinates to array format
    const coordinates: [number, number][] = lot.coordinates.map(coord => {
      if (Array.isArray(coord)) {
        return [coord[0], coord[1]] as [number, number]
      }
      return [coord.lat, coord.lng] as [number, number]
    })

    // AGC: Build request headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    if (userId) {
      headers['x-user-id'] = userId
    }

    logFlowTrace(flowRunId, 'RECOMMEND_CROPS_API_CALL', { 
      coordinatesCount: coordinates.length,
      plantingMonth,
    })

    // AGC: Call backend recommendation API
    const response = await fetch(`${BASE_URL}/api/v1/recommendations`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        lotId,
        fieldId,
        coordinates,
        plantingMonth,
        yearsBack,
        flowRunId, // AGC: [FLOW_TRACE] Propagate for audit
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      logFlowTrace(flowRunId, 'RECOMMEND_CROPS_API_ERROR', { 
        status: response.status, 
        errorData,
      })
      return {
        success: false,
        recommendations: [],
        locationClimate: null,
        targetMonth: plantingMonth || new Date().getMonth() + 1,
        yearsAnalyzed: 0,
        flowRunId,
        error: errorData.message || `API error: ${response.status}`,
      }
    }

    const data = await response.json()
    
    logFlowTrace(flowRunId, 'RECOMMEND_CROPS_SUCCESS', { 
      recommendationsCount: data.data?.recommendations?.length || 0,
    })

    return {
      success: true,
      recommendations: data.data?.recommendations || [],
      locationClimate: data.data?.locationClimate || null,
      targetMonth: data.data?.targetMonth || plantingMonth || new Date().getMonth() + 1,
      yearsAnalyzed: data.data?.yearsAnalyzed || yearsBack,
      flowRunId: data.data?.flowRunId || flowRunId,
    }
  } catch (error) {
    logFlowTrace(flowRunId, 'RECOMMEND_CROPS_EXCEPTION', { 
      error: String(error),
    })
    return {
      success: false,
      recommendations: [],
      locationClimate: null,
      targetMonth: plantingMonth || new Date().getMonth() + 1,
      yearsAnalyzed: 0,
      flowRunId,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

// =============================================================================
// AGC: [SOURCE_OF_TRUTH] CANONICAL FUNCTION #2: generatePlanForLot
// =============================================================================

/**
 * AGC: [SOURCE_OF_TRUTH] Generate a crop plan with tasks and inputs for a lot
 * AGC: This is THE canonical function for plan generation.
 * AGC: Uses cropPlanService.ts for local generation + backend for persistence.
 *
 * @param context - Lot context with IDs and auth
 * @param chosenCrop - The selected crop type
 * @param options - Additional options
 * @returns Generated plan with tasks and inputs
 *
 * @example
 * const result = await generatePlanForLot(
 *   { lotId, fieldId, userId, token, lot },
 *   'soja',
 *   { plantingDate: '2024-10-15' }
 * )
 */
export async function generatePlanForLot(
  context: LotContext,
  chosenCrop: CropType | string,
  options: {
    plantingDate?: string
    flowRunId?: string
  } = {}
): Promise<GeneratePlanResult> {
  const { lotId, fieldId, userId, token, lot } = context
  const { plantingDate = new Date().toISOString().split('T')[0], flowRunId: existingFlowRunId } = options

  // AGC: [FLOW_TRACE] Use existing flowRunId or generate new one
  const flowRunId = existingFlowRunId || generateFlowRunId()
  logFlowTrace(flowRunId, 'GENERATE_PLAN_START', { lotId, fieldId, chosenCrop })

  try {
    // AGC: Validate lot area
    const lotAreaHa = lot?.area || 0
    if (lotAreaHa <= 0) {
      logFlowTrace(flowRunId, 'GENERATE_PLAN_ERROR', { error: 'Invalid lot area' })
      return {
        success: false,
        plan: null,
        inputs: [],
        summary: null,
        tasks: [],
        flowRunId,
        error: 'Lot must have a valid area to generate a plan',
      }
    }

    // AGC: Step 1: Create local crop plan
    logFlowTrace(flowRunId, 'GENERATE_PLAN_CREATE_LOCAL', { chosenCrop, lotAreaHa })
    const localPlan = createCropPlanLocal(
      lotId,
      fieldId,
      chosenCrop as CropType,
      plantingDate,
      lotAreaHa
    )

    // AGC: Step 2: Generate required inputs
    logFlowTrace(flowRunId, 'GENERATE_PLAN_INPUTS', { planId: localPlan.id })
    const inputs = generateRequiredInputs(chosenCrop, localPlan.id, lotId, lotAreaHa)
    const summary = createInputsSummary(inputs, localPlan.id, lotId, chosenCrop as CropType)

    // AGC: Step 3: Generate tasks from crop template
    const tasks = await generateTasksForCrop(
      context,
      chosenCrop,
      plantingDate,
      localPlan.id,
      flowRunId
    )

    // AGC: Step 4: Save plan to backend
    logFlowTrace(flowRunId, 'GENERATE_PLAN_SAVE', { 
      inputsCount: inputs.length,
      tasksCount: tasks.length,
    })

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/crop-plans`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...localPlan,
        inputs,
        tasks: tasks.map(t => t.id),
        flowRunId,
      }),
    })

    if (!response.ok) {
      console.warn('⚠️ AGC: [SOURCE_OF_TRUTH] Failed to save plan to backend, using local plan')
      logFlowTrace(flowRunId, 'GENERATE_PLAN_SAVE_FAILED', { status: response.status })
    } else {
      logFlowTrace(flowRunId, 'GENERATE_PLAN_SAVE_SUCCESS', { planId: localPlan.id })
    }

    // AGC: [AUDIT] Emit plan generated event and comprehensive trace
    try {
      const auditHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (token) {
        auditHeaders['Authorization'] = `Bearer ${token}`
      }
      if (userId) {
        auditHeaders['x-user-id'] = userId
      }

      // AGC: Calculate centroid from lot coordinates
      let centroid = { lat: 0, lng: 0 }
      if (lot?.coordinates && lot.coordinates.length > 0) {
        const coords = lot.coordinates.map(c => 
          Array.isArray(c) ? { lat: c[0], lng: c[1] } : c
        )
        centroid = {
          lat: coords.reduce((sum, c) => sum + c.lat, 0) / coords.length,
          lng: coords.reduce((sum, c) => sum + c.lng, 0) / coords.length,
        }
      }

      // AGC: Group inputs by category
      const inputCategories = [...new Set(inputs.map(i => i.category))].filter(Boolean)
      const taskCategories = [...new Set(tasks.map(t => t.category))].filter(Boolean)

      await fetch(`${BASE_URL}/api/v1/lots/${lotId}/audit-log/plan-generated?fieldId=${fieldId}`, {
        method: 'POST',
        headers: auditHeaders,
        body: JSON.stringify({
          planId: localPlan.id,
          cropType: chosenCrop,
          plantingDate,
          lotName: lot?.name || lotId,
          lotArea: lot?.area || 0,
          taskCount: tasks.length,
          inputCount: inputs.length,
          tasks: tasks.slice(0, 10).map(t => ({
            title: t.title,
            category: t.category,
            startDate: t.startDate,
          })),
          inputs: inputs.slice(0, 15).map(i => ({
            genericName: i.genericName,
            totalQuantity: i.totalQuantity,
            unit: i.unit,
            category: i.category,
          })),
          inputCategories,
          taskCategories,
          geolocation: centroid,
          coordinateCount: lot?.coordinates?.length || 0,
          flowRunId,
        }),
      })
      logFlowTrace(flowRunId, 'GENERATE_PLAN_AUDIT_EMITTED', { planId: localPlan.id })
    } catch (auditError) {
      console.warn('⚠️ AGC: [SOURCE_OF_TRUTH] Failed to emit plan audit:', auditError)
      logFlowTrace(flowRunId, 'GENERATE_PLAN_AUDIT_FAILED', { error: String(auditError) })
      // AGC: Don't fail plan generation if audit fails
    }

    logFlowTrace(flowRunId, 'GENERATE_PLAN_SUCCESS', { 
      planId: localPlan.id,
      inputsCount: inputs.length,
      tasksCount: tasks.length,
    })

    return {
      success: true,
      plan: localPlan,
      inputs,
      summary,
      tasks,
      flowRunId,
    }
  } catch (error) {
    logFlowTrace(flowRunId, 'GENERATE_PLAN_EXCEPTION', { 
      error: String(error),
    })
    return {
      success: false,
      plan: null,
      inputs: [],
      summary: null,
      tasks: [],
      flowRunId,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

// =============================================================================
// AGC: [SOURCE_OF_TRUTH] CANONICAL FUNCTION #3: computeWeatherHealth
// =============================================================================

/**
 * AGC: [SOURCE_OF_TRUTH] Compute weather health for a specific lot
 * AGC: This is THE canonical function for weather health calculation.
 * AGC: Uses backend weather service with EMBRAPA/ZARC methodology.
 *
 * @param context - Lot context with IDs and auth
 * @param options - Additional options
 * @returns Weather health data
 *
 * @example
 * const result = await computeWeatherHealth({
 *   lotId: 'plot-123',
 *   fieldId: 'field-456',
 *   userId: 'user-789',
 *   token: 'auth-token',
 *   lot: plotObject
 * })
 */
export async function computeWeatherHealth(
  context: LotContext,
  options: {
    flowRunId?: string
  } = {}
): Promise<ComputeWeatherHealthResult> {
  const { lotId, fieldId, userId, token, lot } = context
  const { flowRunId: existingFlowRunId } = options

  // AGC: [FLOW_TRACE] Use existing flowRunId or generate new one
  const flowRunId = existingFlowRunId || generateFlowRunId()
  logFlowTrace(flowRunId, 'COMPUTE_WEATHER_START', { lotId, fieldId })

  try {
    // AGC: Validate lot has crop type and coordinates
    if (!lot?.cropType) {
      logFlowTrace(flowRunId, 'COMPUTE_WEATHER_ERROR', { error: 'No crop type' })
      return {
        success: false,
        health: null,
        flowRunId,
        error: 'Lot must have a crop type to calculate weather health',
      }
    }

    if (!lot?.coordinates || lot.coordinates.length < 3) {
      logFlowTrace(flowRunId, 'COMPUTE_WEATHER_ERROR', { error: 'Invalid coordinates' })
      return {
        success: false,
        health: null,
        flowRunId,
        error: 'Lot must have coordinates to calculate weather health',
      }
    }

    // AGC: Calculate centroid from coordinates
    let sumLat = 0
    let sumLng = 0
    const coords = lot.coordinates.map(coord => {
      const lat = Array.isArray(coord) ? coord[0] : coord.lat
      const lng = Array.isArray(coord) ? coord[1] : coord.lng
      sumLat += lat
      sumLng += lng
      return { lat, lng }
    })
    const centroid = {
      lat: sumLat / coords.length,
      lng: sumLng / coords.length,
    }

    // AGC: Build request headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    logFlowTrace(flowRunId, 'COMPUTE_WEATHER_API_CALL', { 
      cropType: lot.cropType,
      centroid,
    })

    // AGC: Call backend weather API
    const response = await fetch(`${BASE_URL}/api/v1/weather/lot-health`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        lotId,
        fieldId,
        cropType: lot.cropType,
        plantingDate: lot.plantingDate || lot.createdAt,
        latitude: centroid.lat,
        longitude: centroid.lng,
        flowRunId,
      }),
    })

    if (!response.ok) {
      // AGC: Try to calculate locally as fallback
      logFlowTrace(flowRunId, 'COMPUTE_WEATHER_API_FAILED', { status: response.status })
      console.warn('⚠️ AGC: [SOURCE_OF_TRUTH] Backend weather API failed, calculating locally')
      
      return await computeWeatherHealthLocally(context, flowRunId)
    }

    const data = await response.json()
    
    logFlowTrace(flowRunId, 'COMPUTE_WEATHER_SUCCESS', { 
      overallScore: data.health?.overallScore,
    })

    // AGC: [AUDIT] Emit weather health computed event and comprehensive trace
    if (data.health) {
      try {
        const auditHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
        }
        if (token) {
          auditHeaders['Authorization'] = `Bearer ${token}`
        }
        if (userId) {
          auditHeaders['x-user-id'] = userId
        }

        await fetch(`${BASE_URL}/api/v1/lots/${lotId}/audit-log/weather-health?fieldId=${fieldId}`, {
          method: 'POST',
          headers: auditHeaders,
          body: JSON.stringify({
            cropId: lot.cropType,
            lotName: lot.name || lotId,
            lotArea: lot.area || 0,
            geolocation: centroid,
            plantingDate: lot.plantingDate || lot.createdAt,
            daysSincePlanting: data.health.daysSincePlanting || 0,
            weatherDateRangeStart: lot.plantingDate || lot.createdAt,
            weatherDateRangeEnd: new Date().toISOString().split('T')[0],
            yearsAnalyzed: 1,
            scores: {
              precipitation: data.health.precipitationScore,
              temperature: data.health.temperatureScore,
              radiation: data.health.radiationScore,
              overall: data.health.overallScore,
            },
            precipitation: {
              required: data.health.precipitationRequired,
              accumulated: data.health.precipitationAccumulated,
              deficit: (data.health.precipitationRequired || 0) - (data.health.precipitationAccumulated || 0),
            },
            temperature: {
              optimalMin: data.health.tempOptimalMin,
              optimalMax: data.health.tempOptimalMax,
              avgReal: data.health.avgTemperature,
              nightsAbove24: data.health.nightsAbove24C,
              nightsBelow10: data.health.nightsBelow10C,
            },
            radiation: {
              required: data.health.radiationRequired,
              accumulated: data.health.radiationAccumulated,
              deficit: (data.health.radiationRequired || 0) - (data.health.radiationAccumulated || 0),
            },
            flowRunId,
          }),
        })
        logFlowTrace(flowRunId, 'COMPUTE_WEATHER_AUDIT_EMITTED', { lotId })
      } catch (auditError) {
        console.warn('⚠️ AGC: [SOURCE_OF_TRUTH] Failed to emit weather audit:', auditError)
        logFlowTrace(flowRunId, 'COMPUTE_WEATHER_AUDIT_FAILED', { error: String(auditError) })
        // AGC: Don't fail weather health if audit fails
      }
    }

    return {
      success: true,
      health: data.health,
      flowRunId,
    }
  } catch (error) {
    logFlowTrace(flowRunId, 'COMPUTE_WEATHER_EXCEPTION', { 
      error: String(error),
    })
    
    // AGC: Try local calculation as fallback
    return await computeWeatherHealthLocally(context, flowRunId)
  }
}

// =============================================================================
// AGC: [SOURCE_OF_TRUTH] HELPER FUNCTIONS
// =============================================================================

/**
 * AGC: Convert a template TaskInput to a TaskInputEntry for user tasks
 * AGC: Calculates totalQuantity from quantityPerHa and lot area
 * AGC: Creates inputSnapshot for historical stability
 */
function convertTemplateInputToTaskInput(
  templateInput: TaskInput,
  lotAreaHa: number
): TaskInputEntry {
  // AGC: Calculate total quantity based on lot area
  const totalQuantity = calculateTotalQuantity(
    templateInput.quantityPerHa,
    lotAreaHa
  )

  // AGC: Create snapshot for historical stability
  const inputSnapshot: InputSnapshot = {
    name: templateInput.inputName,
    category: templateInput.category,
    unit: templateInput.unit,
    capturedAt: new Date().toISOString(),
  }

  return {
    inputId: templateInput.inputId,
    inputName: templateInput.inputName,
    quantityPerHa: templateInput.quantityPerHa,
    totalQuantity,
    unit: templateInput.unit,
    inputSnapshot,
    isCustom: false,
    isDiscontinued: false,
  }
}

/**
 * AGC: TaskTemplate structure from backend API
 * AGC: Matches the structure in admin/models.ts
 */
interface BackendTaskTemplate {
  id?: string
  title: string
  category: string
  daysFromPlanting?: number
  daysAfterPlanting?: number  // AGC: Legacy field name
  inputs?: TaskInput[]
  order?: number
  isRequired?: boolean
}

/**
 * AGC: Generate tasks for a specific crop type
 * AGC: Uses backend task templates and copies inputs with quantities
 * AGC: CRITICAL FIX: Now properly copies inputs from templates to generated tasks
 */
async function generateTasksForCrop(
  context: LotContext,
  cropType: CropType | string,
  plantingDate: string,
  planId: string,
  flowRunId: string
): Promise<GeneratedTask[]> {
  const { lotId, fieldId, token, lot } = context
  
  // AGC: Get lot area for quantity calculations
  const lotAreaHa = lot?.area || 0
  if (lotAreaHa <= 0) {
    console.warn('⚠️ AGC: [SOURCE_OF_TRUTH] Lot area is 0 or undefined, input quantities may be incorrect')
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // AGC: Try to get task templates from backend
    const response = await fetch(`${BASE_URL}/api/v1/crop-plans/templates/${cropType}`, {
      method: 'GET',
      headers,
    })

    if (response.ok) {
      const data = await response.json()
      const templates: BackendTaskTemplate[] = data.tasks || []
      
      logFlowTrace(flowRunId, 'GENERATE_TASKS_TEMPLATES_LOADED', { 
        cropType, 
        templateCount: templates.length,
        lotAreaHa,
      })
      
      // AGC: Convert templates to tasks WITH INPUTS
      // AGC: Use lot creation date as minimum - no tasks should be scheduled before lot exists
      const lotCreationDate = new Date(lot?.createdAt || new Date().toISOString())
      const today = new Date()
      const minDate = lotCreationDate > today ? today : lotCreationDate

      return templates.map((template, index: number) => {
        const taskDate = new Date(plantingDate)
        const daysOffset = template.daysFromPlanting ?? template.daysAfterPlanting ?? (index * 7)
        taskDate.setDate(taskDate.getDate() + daysOffset)

        // AGC: CRITICAL - Ensure no task is scheduled before lot creation
        // AGC: Pre-planting tasks (negative offset) get staggered from minDate
        if (taskDate < minDate) {
          const staggerDays = Math.max(0, index)
          taskDate.setTime(minDate.getTime())
          taskDate.setDate(taskDate.getDate() + staggerDays)
        }
        
        // AGC: CRITICAL FIX - Convert template inputs to task inputs
        const taskInputs: TaskInputEntry[] = (template.inputs || []).map(templateInput => 
          convertTemplateInputToTaskInput(templateInput, lotAreaHa)
        )
        
        const generatedTask: GeneratedTask = {
          id: `task-${planId}-${index}-${Date.now()}`,
          title: template.title,
          category: template.category,
          startDate: taskDate.toISOString().split('T')[0],
          endDate: taskDate.toISOString().split('T')[0],
          status: 'pending',
          plotId: lotId,
          fieldId: fieldId,
          lotId: lotId,                           // AGC: Link to lot for area reference
          templateId: template.id,                 // AGC: Track source template
          inputs: taskInputs,                      // AGC: CRITICAL - Include inputs!
          order: template.order ?? index,          // AGC: Task order in sequence
        }
        
        // AGC: Log if inputs were copied
        if (taskInputs.length > 0) {
          console.log(`✅ AGC: Task "${template.title}" generated with ${taskInputs.length} inputs from template`)
        }
        
        return generatedTask
      })
    }
  } catch (error) {
    console.warn('⚠️ AGC: [SOURCE_OF_TRUTH] Could not fetch task templates from backend:', error)
  }

  // AGC: Return empty array - tasks will be generated by FieldTaskPlanning component
  logFlowTrace(flowRunId, 'GENERATE_TASKS_LOCAL_FALLBACK', { cropType })
  return []
}

/**
 * AGC: Compute weather health locally as fallback
 * AGC: Uses the same logic as useLotWeatherHealth hook
 */
async function computeWeatherHealthLocally(
  context: LotContext,
  flowRunId: string
): Promise<ComputeWeatherHealthResult> {
  const { lot } = context

  logFlowTrace(flowRunId, 'COMPUTE_WEATHER_LOCAL', { lotId: context.lotId })

  try {
    // AGC: Import weather calculation functions
    const { getWeatherData } = await import('./weatherService')
    const { 
      CROP_WEATHER_REQUIREMENTS,
      getCurrentGrowthStage,
      calculateOverallScore,
    } = await import('../types')

    if (!lot?.cropType || !lot?.coordinates) {
      return {
        success: false,
        health: null,
        flowRunId,
        error: 'Missing crop type or coordinates for local calculation',
      }
    }

    // AGC: Get crop requirements
    const cropReqs = CROP_WEATHER_REQUIREMENTS[lot.cropType as keyof typeof CROP_WEATHER_REQUIREMENTS]
    if (!cropReqs) {
      return {
        success: false,
        health: null,
        flowRunId,
        error: `No weather requirements defined for crop: ${lot.cropType}`,
      }
    }

    // AGC: Calculate centroid
    const coords = lot.coordinates.map(coord => 
      Array.isArray(coord) ? { lat: coord[0], lng: coord[1] } : coord
    )
    const centroid = {
      lat: coords.reduce((sum, c) => sum + c.lat, 0) / coords.length,
      lng: coords.reduce((sum, c) => sum + c.lng, 0) / coords.length,
    }

    // AGC: Get weather data
    const plantingDate = lot.plantingDate || lot.createdAt || new Date().toISOString()
    const weatherData = await getWeatherData(
      centroid.lat,
      centroid.lng,
      plantingDate,
      cropReqs.cycleDays
    )

    if (!weatherData) {
      return {
        success: false,
        health: null,
        flowRunId,
        error: 'Could not fetch weather data',
      }
    }

    // AGC: Calculate scores
    const growthStage = getCurrentGrowthStage(plantingDate, cropReqs.cycleDays)
    const overallScore = calculateOverallScore(
      weatherData.precipitationScore || 50,
      weatherData.temperatureScore || 50,
      weatherData.radiationScore || 50
    )

    const health: LotWeatherHealth = {
      lotId: context.lotId,
      cropType: lot.cropType,
      plantingDate,
      cycleDays: cropReqs.cycleDays,
      daysSincePlanting: Math.floor(
        (Date.now() - new Date(plantingDate).getTime()) / (1000 * 60 * 60 * 24)
      ),
      currentGrowthStage: growthStage,
      overallScore,
      precipitationScore: weatherData.precipitationScore || 50,
      temperatureScore: weatherData.temperatureScore || 50,
      radiationScore: weatherData.radiationScore || 50,
      precipitationAccumulated: weatherData.precipitationAccumulated || 0,
      precipitationRequired: cropReqs.precipitationTotal,
      avgTemperature: weatherData.avgTemperature || 25,
      tempOptimalMin: cropReqs.tempOptimalMin,
      tempOptimalMax: cropReqs.tempOptimalMax,
      radiationAccumulated: weatherData.radiationAccumulated || 0,
      radiationRequired: cropReqs.radiationTotal,
      nightsAbove24C: weatherData.nightsAbove24C || 0,
      nightsBelow10C: weatherData.nightsBelow10C || 0,
      lastUpdated: new Date().toISOString(),
      dataSource: 'local',
    }

    logFlowTrace(flowRunId, 'COMPUTE_WEATHER_LOCAL_SUCCESS', { 
      overallScore: health.overallScore,
    })

    return {
      success: true,
      health,
      flowRunId,
    }
  } catch (error) {
    logFlowTrace(flowRunId, 'COMPUTE_WEATHER_LOCAL_ERROR', { 
      error: String(error),
    })
    return {
      success: false,
      health: null,
      flowRunId,
      error: error instanceof Error ? error.message : 'Local calculation failed',
    }
  }
}

// =============================================================================
// AGC: [SOURCE_OF_TRUTH] EXPORTS
// =============================================================================

export default {
  recommendCropsForLot,
  generatePlanForLot,
  computeWeatherHealth,
  DEV_FLAGS,
}

